import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { L, SaveBtn, Card, fmtDate, DateInput } from "./shared";

export const STATUS = ["On Track", "Attention Required", "Delayed", "Completed"];
const HEALTH_LEVELS = ["On Track", "Attention Required", "Critical"];
const HEALTH_DIMS = [["schedule", "Program"], ["cost", "Cost"], ["risk", "Risc"], ["decisions", "Decizii"], ["quality", "Calitate"]];
const STAGE_STATUS = ["Upcoming", "In Progress", "Completed", "Delayed"];
export const ACTION_KINDS = [["approval", "Aprobare"], ["decision", "Decizie"], ["confirmation", "Confirmare"], ["selection", "Selectare variantă"], ["info", "Furnizare informații"], ["validation", "Validare document"], ["change_request", "Change request"]];
const lvlColor = { "On Track": "text-emerald-600", "Attention Required": "text-amber-600", "Critical": "text-red-600", "Delayed": "text-red-600" };

export function StatusPanel({ pid, cfg, onSaved }) {
  const [c, setC] = useState({ client_status: cfg.client_status, client_progress: cfg.client_progress, current_stage: cfg.current_stage, estimated_completion: cfg.estimated_completion, health: cfg.health });
  const [saving, setSaving] = useState(false);
  const up = (patch) => setC(x => ({ ...x, ...patch }));
  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/staff/client-config/${pid}`, { ...c, client_progress: Number(c.client_progress) || 0 });
      onSaved(data); toast.success("Salvat");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }
  return (
    <Card className="space-y-5" data-testid="cc-status">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <L label="Status general"><select value={c.client_status} onChange={e => up({ client_status: e.target.value })} data-testid="cc-status-select" className="inp">{STATUS.map(s => <option key={s}>{s}</option>)}</select></L>
        <L label="Progres (%)"><input type="number" min="0" max="100" value={c.client_progress ?? 0} onChange={e => up({ client_progress: e.target.value })} className="inp" data-testid="cc-progress" /></L>
        <L label="Etapa curentă"><input value={c.current_stage || ""} onChange={e => up({ current_stage: e.target.value })} className="inp" placeholder="Ex: Tender & Contractor Selection" /></L>
        <L label="Termen estimat finalizare"><DateInput value={c.estimated_completion || ""} onChange={v => up({ estimated_completion: v })} testid="cc-est-completion" /></L>
      </div>
      <div>
        <div className="font-display font-semibold text-ink mb-2">Indicator de sănătate (declarat de PM)</div>
        <div className="space-y-2">
          {HEALTH_DIMS.map(([k, label]) => {
            const h = (c.health || {})[k] || { level: "On Track", note: "" };
            return (
              <div key={k} className="grid grid-cols-[120px_180px_1fr] gap-3 items-center" data-testid={`cc-health-${k}`}>
                <span className="text-sm font-medium text-ink">{label}</span>
                <select value={h.level} onChange={e => up({ health: { ...c.health, [k]: { ...h, level: e.target.value } } })} className={`inp ${lvlColor[h.level] || ""}`}>{HEALTH_LEVELS.map(l => <option key={l}>{l}</option>)}</select>
                <input value={h.note} onChange={e => up({ health: { ...c.health, [k]: { ...h, note: e.target.value } } })} className="inp" placeholder="Justificare (opțional)" />
              </div>
            );
          })}
        </div>
      </div>
      <SaveBtn onClick={save} saving={saving} />
    </Card>
  );
}

export function TimelinePanel({ pid, cfg, onSaved }) {
  const [timeline, setTimeline] = useState(cfg.timeline || []);
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try { const { data } = await api.put(`/staff/client-config/${pid}`, { timeline }); onSaved(data); toast.success("Salvat"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }
  const setRow = (i, patch) => setTimeline(t => t.map((r, j) => j === i ? { ...r, ...patch } : r));
  return (
    <Card className="space-y-3" data-testid="cc-timeline">
      {timeline.map((st, i) => (
        <div key={st.stage} className="grid grid-cols-[130px_170px_1fr_90px] gap-3 items-center">
          <span className="text-sm font-medium text-ink">{st.stage}</span>
          <select value={st.status} onChange={e => setRow(i, { status: e.target.value })} className="inp">{STAGE_STATUS.map(s => <option key={s}>{s}</option>)}</select>
          <input value={st.period || ""} onChange={e => setRow(i, { period: e.target.value })} className="inp" placeholder="Perioadă (ex: Iun–Aug 2026)" />
          <input type="number" min="0" max="100" value={st.progress || 0} onChange={e => setRow(i, { progress: Number(e.target.value) })} className="inp" placeholder="%" />
        </div>
      ))}
      <SaveBtn onClick={save} saving={saving} />
    </Card>
  );
}

export function ActionsPanel({ pid, cfg, reload }) {
  const empty = { kind: "approval", title: "", description: "", deadline: "", cost_impact: "", schedule_impact: "", recommendation: "" };
  const [f, setF] = useState(empty); const [busy, setBusy] = useState(false);
  async function add() {
    if (!f.title.trim()) { toast.error("Titlu obligatoriu"); return; }
    setBusy(true);
    try { await api.post(`/staff/client-config/${pid}/actions`, f); setF(empty); reload(); toast.success("Adăugat"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  async function del(id) { await api.delete(`/staff/client-config/${pid}/actions/${id}`); reload(); }
  return (
    <div className="space-y-4" data-testid="cc-actions">
      <Card className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <L label="Tip"><select value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} className="inp">{ACTION_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></L>
        <L label="Deadline"><DateInput value={f.deadline} onChange={v => setF({ ...f, deadline: v })} /></L>
        <div className="md:col-span-2"><L label="Titlu"><input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="cc-action-title" className="inp" placeholder="Ex: Approval required - Contractor shortlist" /></L></div>
        <div className="md:col-span-2"><L label="Descriere"><input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="inp" /></L></div>
        <L label="Impact cost"><input value={f.cost_impact} onChange={e => setF({ ...f, cost_impact: e.target.value })} className="inp" placeholder="Ex: +12.000 €" /></L>
        <L label="Impact program"><input value={f.schedule_impact} onChange={e => setF({ ...f, schedule_impact: e.target.value })} className="inp" placeholder="Ex: +10 zile" /></L>
        <div className="md:col-span-2"><L label="Recomandare"><input value={f.recommendation} onChange={e => setF({ ...f, recommendation: e.target.value })} className="inp" /></L></div>
        <div><button onClick={add} disabled={busy} data-testid="cc-action-add" className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Adaugă</button></div>
      </Card>
      {(cfg.actions || []).map(a => (
        <Card key={a.id} className="p-4" data-testid={`cc-action-${a.id}`}>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase bg-brand/10 text-brand px-1.5 py-0.5 rounded">{(ACTION_KINDS.find(k => k[0] === a.kind) || [])[1] || a.kind}</span>
                <span className={`text-[11px] font-medium ${a.status === "approved" ? "text-emerald-600" : a.status === "rejected" ? "text-red-600" : "text-amber-600"}`}>{a.status}</span>
                {a.deadline && <span className="text-[11px] text-ink-muted">· deadline {fmtDate(a.deadline)}</span>}
              </div>
              <div className="font-medium text-ink mt-1">{a.title}</div>
              {a.description && <div className="text-[13px] text-ink-muted">{a.description}</div>}
              <div className="text-[12px] text-ink-muted mt-1 flex flex-wrap gap-x-4">
                {a.cost_impact && <span>Cost: {a.cost_impact}</span>}
                {a.schedule_impact && <span>Program: {a.schedule_impact}</span>}
                {a.recommendation && <span>Recomandare: {a.recommendation}</span>}
              </div>
              {(a.log || []).length > 0 && (
                <div className="mt-2 border-t border-neutral-100 pt-2 text-[12px] text-ink-muted">
                  <div className="font-mono text-[10px] uppercase tracking-widest mb-1">Registru decizii</div>
                  {a.log.map((e, i) => <div key={i}>v{e.version} · {e.who} · {e.what}{e.comment ? ` — „${e.comment}"` : ""} · {fmtDate(e.when)}</div>)}
                </div>
              )}
            </div>
            <button onClick={() => del(a.id)} className="text-ink-muted hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function UpdatesPanel({ pid, cfg, reload }) {
  const [f, setF] = useState({ progress_this_week: "", next_steps: "", client_actions: "" }); const [busy, setBusy] = useState(false);
  async function add() {
    setBusy(true);
    try { await api.post(`/staff/client-config/${pid}/updates`, f); setF({ progress_this_week: "", next_steps: "", client_actions: "" }); reload(); toast.success("Publicat"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  async function del(id) { await api.delete(`/staff/client-config/${pid}/updates/${id}`); reload(); }
  return (
    <div className="space-y-4" data-testid="cc-updates">
      <Card className="p-5 space-y-3">
        <L label="Progres săptămâna aceasta"><textarea rows={2} value={f.progress_this_week} onChange={e => setF({ ...f, progress_this_week: e.target.value })} data-testid="cc-update-progress" className="inp" /></L>
        <L label="Pași următori"><textarea rows={2} value={f.next_steps} onChange={e => setF({ ...f, next_steps: e.target.value })} className="inp" /></L>
        <L label="Ce așteptăm de la client"><textarea rows={2} value={f.client_actions} onChange={e => setF({ ...f, client_actions: e.target.value })} className="inp" /></L>
        <button onClick={add} disabled={busy} data-testid="cc-update-add" className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publică update</button>
      </Card>
      {(cfg.updates || []).map(u => (
        <Card key={u.id} className="p-4 text-[13px]">
          <div className="flex justify-between mb-2"><span className="font-mono text-[10px] uppercase text-ink-muted">{fmtDate(u.at)} · {u.by}</span><button onClick={() => del(u.id)} className="text-ink-muted hover:text-red-600"><Trash2 className="h-4 w-4" /></button></div>
          {u.progress_this_week && <p><b>Progres:</b> {u.progress_this_week}</p>}
          {u.next_steps && <p><b>Pași următori:</b> {u.next_steps}</p>}
          {u.client_actions && <p><b>Așteptăm de la client:</b> {u.client_actions}</p>}
        </Card>
      ))}
    </div>
  );
}

export function MeetingsPanel({ pid, cfg, reload }) {
  const empty = { title: "", when: "", kind: "upcoming", agenda: "", minutes: "", decisions: "", action_items: "" };
  const [f, setF] = useState(empty); const [busy, setBusy] = useState(false);
  async function add() {
    if (!f.title.trim()) { toast.error("Titlu obligatoriu"); return; }
    setBusy(true);
    try { await api.post(`/staff/client-config/${pid}/meetings`, f); setF(empty); reload(); toast.success("Adăugat"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  async function del(id) { await api.delete(`/staff/client-config/${pid}/meetings/${id}`); reload(); }
  return (
    <div className="space-y-4" data-testid="cc-meetings">
      <Card className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <L label="Titlu"><input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="cc-meeting-title" className="inp" /></L>
        <L label="Data/ora"><input type="datetime-local" value={f.when} onChange={e => setF({ ...f, when: e.target.value })} className="inp" /></L>
        <L label="Tip"><select value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} className="inp"><option value="upcoming">Viitoare</option><option value="past">Anterioară</option></select></L>
        <div className="md:col-span-2"><L label="Agenda"><textarea rows={2} value={f.agenda} onChange={e => setF({ ...f, agenda: e.target.value })} className="inp" /></L></div>
        <div className="md:col-span-2"><L label="Minută"><textarea rows={2} value={f.minutes} onChange={e => setF({ ...f, minutes: e.target.value })} className="inp" /></L></div>
        <L label="Decizii"><textarea rows={2} value={f.decisions} onChange={e => setF({ ...f, decisions: e.target.value })} className="inp" /></L>
        <L label="Acțiuni de urmărit"><textarea rows={2} value={f.action_items} onChange={e => setF({ ...f, action_items: e.target.value })} className="inp" /></L>
        <div><button onClick={add} disabled={busy} data-testid="cc-meeting-add" className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Adaugă</button></div>
      </Card>
      {(cfg.meetings || []).map(m => (
        <Card key={m.id} className="p-4 text-[13px]">
          <div className="flex justify-between"><span className="font-medium text-ink">{m.title} <span className="text-[10px] font-mono uppercase text-ink-muted">· {m.kind === "past" ? "anterioară" : "viitoare"}{m.when ? ` · ${String(m.when).replace("T", " ")}` : ""}</span></span><button onClick={() => del(m.id)} className="text-ink-muted hover:text-red-600"><Trash2 className="h-4 w-4" /></button></div>
          {m.agenda && <p className="mt-1"><b>Agenda:</b> {m.agenda}</p>}
          {m.minutes && <p><b>Minută:</b> {m.minutes}</p>}
          {m.decisions && <p><b>Decizii:</b> {m.decisions}</p>}
          {m.action_items && <p><b>Acțiuni:</b> {m.action_items}</p>}
        </Card>
      ))}
    </div>
  );
}
